import { useEffect, useState } from 'react'
import {
    listDispatch, markDispatchSent, undoDispatchSent, updateDispatchAddress,
    dispatchAddressesUrl, dispatchCertificateUrl, type DispatchRow, type DispatchShipping,
} from '../api'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import DataTable, { type Column } from '../components/DataTable'
import Drawer from '../components/Drawer'
import CountryPicker from '../components/CountryPicker'
import { fill, PAGES_CERTIFICATE_DISPATCH_LIST_TEXTS as T } from '../../shared/texts'

// The work list for the person who posts the certificates. Deliberately a short, plain list
// with no pagination and no server-side sorting: it holds what is paid and not yet in the
// post, which is a handful of rows. A list that needs paging here would mean something else
// has gone wrong.

function addressText(r: DispatchRow): string {
    const s = r.shipping
    if (!s) return '—'
    const name = [s.firstName, s.lastName].filter(Boolean).join(' ')
    return [s.company, name, s.line1, s.line2, [s.postalCode, s.city].filter(Boolean).join(' ')]
        .filter(Boolean).join(', ')
}

function dateText(iso?: string): string {
    if (!iso) return '—'
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE')
}

export default function CertificateDispatchList() {
    const { openTab } = useTabs()
    // A sale queues a certificate, another client ticks one off or fixes an address.
    const version = useDataVersion('certificates')
    const { toast } = useToast()
    const { can } = useAuth()
    const canWrite = can('certificates', 'write')

    const [pending, setPending] = useState<DispatchRow[]>([])
    const [recent, setRecent] = useState<DispatchRow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [selected, setSelected] = useState<Set<string>>(new Set())
    // Which label on the sheet to start at. Lives here and not in the settings: it changes
    // with every print, and a value you have to go and edit elsewhere is a value nobody sets.
    const [startLabel, setStartLabel] = useState(1)
    const [busy, setBusy] = useState('')
    // Die Zeile, deren Anschrift gerade bearbeitet wird (null = Schublade zu).
    const [editing, setEditing] = useState<DispatchRow | null>(null)
    const [addr, setAddr] = useState<DispatchShipping>({})
    const [addrBusy, setAddrBusy] = useState(false)
    const [addrError, setAddrError] = useState('')

    const failMsg = (msg: string) => { setError(msg); toast(msg, 'err') }

    const load = () => {
        setLoading(true)
        listDispatch()
            .then((d) => {
                setPending(d.pending); setRecent(d.recent); setError('')
                // Drop selections for rows that are gone, or "print addresses" would silently
                // include somebody who was already dealt with in another tab.
                setSelected((prev) => new Set(d.pending.filter((r) => prev.has(r.id)).map((r) => r.id)))
            })
            .catch((e) => failMsg(e?.response?.data?.error ?? T.loadFailed))
            .finally(() => setLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [version])

    const toggle = (id: string) => setSelected((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id); else next.add(id)
        return next
    })
    // Only rows with a usable address can be selected — the address print skips the others
    // anyway, and a selected row that produces nothing is a lie about what will come out.
    const selectable = pending.filter((r) => r.addressComplete)
    const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.id))
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map((r) => r.id)))

    const openPdf = (url: string, name: string, id: string, fname = `${name}.pdf`) =>
        openTab({ id, name, component: 'file-viewer', config: { url, fname } })

    const printAddresses = () => {
        const ids = pending.filter((r) => selected.has(r.id) && r.addressComplete).map((r) => r.id)
        if (!ids.length) { toast(T.noUsableSelected, 'err'); return }
        openPdf(dispatchAddressesUrl(ids, startLabel), fill(T.addressesTabName, { n: ids.length }), 'dispatch-addresses')
    }

    const openAddress = (row: DispatchRow) => {
        setEditing(row)
        // Mit dem, was da ist, vorbelegen — korrigiert wird meist EIN Feld, und alles neu
        // tippen zu müssen ist der schnellste Weg zu einem zweiten Fehler.
        setAddr({ ...(row.shipping ?? {}) })
        setAddrError('')
    }

    async function saveAddress() {
        if (!editing) return
        setAddrBusy(true); setAddrError('')
        try {
            const row = await updateDispatchAddress(editing.id, addr)
            toast(row.addressComplete
                ? fill(T.addressSavedComplete, { num: row.certificateNumber })
                : T.addressSavedIncomplete)
            setEditing(null)
            load()
        } catch (e: any) {
            setAddrError(e?.response?.data?.error ?? T.saveFailed)
        } finally {
            setAddrBusy(false)
        }
    }

    async function act(row: DispatchRow, what: 'sent' | 'undo') {
        setBusy(row.id)
        try {
            if (what === 'sent') { await markDispatchSent(row.id); toast(fill(T.markedSent, { num: row.certificateNumber })) }
            else { await undoDispatchSent(row.id); toast(fill(T.backOnList, { num: row.certificateNumber })) }
            load()
        } catch (e: any) {
            failMsg(e?.response?.data?.error ?? T.actionFailed)
        } finally {
            setBusy('')
        }
    }

    const columns: Column<DispatchRow>[] = [
        {
            key: 'sel', header: allSelected ? '☑' : '☐', defaultWidth: 40, className: 'center',
            render: (r) => (
                <input type="checkbox" checked={selected.has(r.id)} disabled={!r.addressComplete}
                    onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()}
                    aria-label={fill(T.selectAria, { num: r.certificateNumber })} />
            ),
        },
        { key: 'holder', header: T.colHolder, defaultWidth: 150, render: (r) => r.holderName },
        { key: 'course', header: T.colCourse, defaultWidth: 180, render: (r) => r.courseTitle },
        { key: 'number', header: T.colNumber, defaultWidth: 110, render: (r) => r.certificateNumber },
        { key: 'ordered', header: T.colOrdered, defaultWidth: 90, render: (r) => dateText(r.orderedAt) },
        {
            key: 'address', header: T.address, defaultWidth: 250,
            render: (r) => (
                <span>
                    {r.addressComplete
                        ? addressText(r)
                        // Not hidden and not silently skipped: this row is paid and
                        // undeliverable, which is precisely the one somebody has to do
                        // something about.
                        : <span className="notice err" style={{ padding: '1px 6px' }}>{T.addressIncomplete}{addressText(r)}</span>}
                    {r.addressChanges > 0 && (
                        <span className="hint" title={fill(T.changedByTitle, { by: r.addressChangedBy ?? '—', date: dateText(r.addressChangedAt) })}>
                            {' '}{T.handCorrected}
                        </span>
                    )}
                </span>
            ),
        },
        {
            // Drei Knöpfe (PDF, Anschrift, Versandt): bei 190 px rutschte „PDF“ nach links unter die Anschrift und
            // war nicht mehr anklickbar (gefunden 31.10.2026 beim Test zu 58a).
            key: 'actions', header: T.colActions, defaultWidth: 270, sticky: true,
            render: (r) => (
                <div className="row-actions">
                    <button type="button" className="icon-action" title={T.printCertificateTitle}
                        onClick={() => openPdf(dispatchCertificateUrl(r.id, r.pdfName), r.pdfName, `cert-${r.id}`, r.pdfName)}>
                        {T.pdf}
                    </button>
                    {canWrite && (
                        <button type="button" onClick={() => openAddress(r)}>{T.address}</button>
                    )}
                    {canWrite && (
                        <button type="button" disabled={busy === r.id || !r.addressComplete}
                            title={r.addressComplete ? undefined : T.completeAddressFirst}
                            onClick={() => act(r, 'sent')}>
                            {busy === r.id ? '…' : T.sent}
                        </button>
                    )}
                </div>
            ),
        },
    ]

    const recentColumns: Column<DispatchRow>[] = [
        { key: 'holder', header: T.colHolder, defaultWidth: 150, render: (r) => r.holderName },
        { key: 'number', header: T.colNumber, defaultWidth: 110, render: (r) => r.certificateNumber },
        { key: 'sentAt', header: T.colSentAt, defaultWidth: 110, render: (r) => dateText(r.sentAt) },
        { key: 'sentBy', header: T.colSentBy, defaultWidth: 130, render: (r) => r.sentBy || '—' },
        {
            key: 'actions', header: '', defaultWidth: 130, sticky: true,
            render: (r) => canWrite ? (
                <div className="row-actions">
                    <button type="button" disabled={busy === r.id} onClick={() => act(r, 'undo')}>
                        {busy === r.id ? '…' : T.undo}
                    </button>
                </div>
            ) : null,
        },
    ]

    return (
        <div className="list-page">
            <ListToolbar
                title={T.title}
                infoText={T.info}
            />

            {error && <div className="notice err">{error}</div>}

            <div className="form-row" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', margin: '8px 0' }}>
                <button type="button" onClick={toggleAll} disabled={!selectable.length}>
                    {allSelected ? T.deselectAll : T.selectAll}
                </button>
                <div className="field" style={{ margin: 0 }}>
                    <label>{T.startLabel}</label>
                    <input type="number" min={1} value={startLabel} style={{ width: 90 }}
                        onChange={(e) => setStartLabel(Math.max(1, Number(e.target.value) || 1))} />
                </div>
                <button type="button" onClick={printAddresses} disabled={!selected.size}>
                    {fill(T.printAddresses, { n: selected.size })}
                </button>
                <button type="button" onClick={load}>{T.reload}</button>
            </div>

            <ListPanel count={pending.length} grandTotal={pending.length} filtered={false}>
                {loading ? <p className="muted">{T.loading}</p> : pending.length === 0 ? (
                    <p className="muted">{T.nothingToSend}</p>
                ) : (
                    <DataTable tableId="certificate-dispatch" columns={columns} rows={pending} rowKey={(r) => r.id} />
                )}
            </ListPanel>

            <Drawer
                open={!!editing}
                title={editing ? fill(T.drawerTitle, { holder: editing.holderName, num: editing.certificateNumber }) : ''}
                onClose={() => { if (!addrBusy) setEditing(null) }}
                width={480}
                footer={
                    <>
                        <button type="button" onClick={saveAddress} disabled={addrBusy}>
                            {addrBusy ? '…' : T.save}
                        </button>
                        <button type="button" className="ghost" onClick={() => setEditing(null)} disabled={addrBusy}>
                            {T.cancel}
                        </button>
                    </>
                }
            >
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.drawerIntro}
                </p>
                {addrError && <div className="notice err">{addrError}</div>}
                {editing && editing.addressChanges > 0 && (
                    <div className="notice">
                        {fill(T.changedNotice, { count: editing.addressChanges, by: editing.addressChangedBy ?? '—', date: dateText(editing.addressChangedAt) })}
                    </div>
                )}

                <div className="field"><label>{T.company}</label>
                    <input type="text" value={addr.company ?? ''} onChange={(e) => setAddr({ ...addr, company: e.target.value })} /></div>
                <div className="row">
                    <div className="field"><label>{T.firstName}</label>
                        <input type="text" value={addr.firstName ?? ''} onChange={(e) => setAddr({ ...addr, firstName: e.target.value })} /></div>
                    <div className="field"><label>{T.lastName}</label>
                        <input type="text" value={addr.lastName ?? ''} onChange={(e) => setAddr({ ...addr, lastName: e.target.value })} /></div>
                </div>
                <div className="field"><label>{T.street}</label>
                    <input type="text" value={addr.line1 ?? ''} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} /></div>
                <div className="field"><label>{T.line2}</label>
                    <input type="text" value={addr.line2 ?? ''} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} /></div>
                <div className="row">
                    <div className="field"><label>{T.postalCode}</label>
                        <input type="text" style={{ width: 110 }} value={addr.postalCode ?? ''}
                            onChange={(e) => setAddr({ ...addr, postalCode: e.target.value })} /></div>
                    <div className="field"><label>{T.city}</label>
                        <input type="text" value={addr.city ?? ''} onChange={(e) => setAddr({ ...addr, city: e.target.value })} /></div>
                    <div className="field"><label>{T.country}</label>
                        <CountryPicker name="dispatch-country" value={addr.country ?? ''}
                            onChange={(country) => setAddr({ ...addr, country })} /></div>
                </div>
                <p className="muted">
                    {T.requiredHint}
                </p>
            </Drawer>

            {recent.length > 0 && (
                <>
                    <h3 style={{ marginTop: 24 }}>{T.recentTitle}</h3>
                    <p className="muted" style={{ marginTop: 0 }}>
                        {T.recentHint}
                    </p>
                    <ListPanel count={recent.length} grandTotal={recent.length} filtered={false}>
                        <DataTable tableId="certificate-dispatch-sent" columns={recentColumns} rows={recent} rowKey={(r) => r.id} />
                    </ListPanel>
                </>
            )}
        </div>
    )
}
